import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2, Star, ChevronLeft, ChevronRight, Phone, Mail, ExternalLink } from "lucide-react";
import { HERO_SLIDES, COMPANY_INFO, GALLERIES } from "../data";
import { fetchPageById, fetchGalleryById, fetchHeroSlides, fetchCompanyInfo, fetchServices, fetchBenefits } from "../lib/cms";
import { getOptimizedImageUrl } from "../lib/utils";
import ContactForm from "../components/ContactForm";

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(COMPANY_INFO);
  const [heroSlides, setHeroSlides] = useState(HERO_SLIDES);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [projectSlide, setProjectSlide] = useState(0);

  // Default services for instant render
  const defaultServicesOverview = [
    {
      title: "Totalrenovering",
      desc: "Vi forvandler ældre boliger til moderne drømmehjem med respekt for den oprindelige stil.",
      path: "/totalrenovering",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image002-2.jpg"
    },
    {
      title: "Ombygning",
      desc: "Skab mere lys, luft og plads med gennemtænkte ombygninger og loft til kip.",
      path: "/ombygning",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00006-2-scaled.jpeg"
    },
    {
      title: "Totalentreprise",
      desc: "Vi styrer dit byggeprojekt sikkert fra start til slut. Én fast kontaktperson, nul bøvl.",
      path: "/totalentreprise",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00003-scaled.jpeg"
    },
    {
      title: "Tilbygninger",
      desc: "Mangler du plads? Vi bygger harmoniske tilbygninger, der passer perfekt til din eksisterende bolig.",
      path: "/ydelser/tilbygninger",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00005-3-scaled.jpeg"
    }
  ];

  const defaultSecondaryServices = [
    { title: "Træterrasser", path: "/ydelser/traeterrasser" },
    { title: "Døre og vinduer", path: "/ydelser/doere-og-vinduer" },
    { title: "Tagarbejde", path: "/ydelser/tagarbejde" },
    { title: "Kontakt os", path: "/kontakt", highlight: true }
  ];

  const [servicesOverview, setServicesOverview] = useState(defaultServicesOverview);
  const [secondaryServices, setSecondaryServices] = useState(defaultSecondaryServices);

  // Load CMS data
  useEffect(() => {
    async function loadData() {
      const [page, info, slides, allServices] = await Promise.all([
        fetchPageById("index"),
        fetchCompanyInfo(),
        fetchHeroSlides(),
        fetchServices(),
      ]);
      setPageData(page);
      if (info) setCompanyInfo(info);
      if (slides && slides.length > 0) setHeroSlides(slides);

      if (allServices && allServices.length > 0) {
        const featured = allServices.filter((s) => s.highlight);
        if (featured.length > 0) {
          setServicesOverview(featured);
        }
        const nonFeatured = allServices.filter((s) => !s.highlight);
        if (nonFeatured.length > 0) {
          setSecondaryServices([
            ...nonFeatured.map((s) => ({ title: s.title, path: s.path })),
            { title: "Kontakt os", path: "/kontakt", highlight: true },
          ]);
        }
      }

      if (page && page.gallery_slug) {
        const gallery = await fetchGalleryById(page.gallery_slug);
        setGalleryData(gallery);
      }
    }
    loadData();
  }, []);

  // Hero slider auto-play
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  useEffect(() => {
    const timer = setInterval(() => {
      setProjectSlide((prev) => prev + 1);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const activeSlide = heroSlides[currentSlide] || heroSlides[0] || HERO_SLIDES[0];

  const heroMosaicColumns = [
    {
      animationClass: "lg:motion-safe:animate-[hero-mosaic-up_36s_linear_infinite]",
      tiles: [
        {
          src: activeSlide.image,
          alt: activeSlide.title,
          ratio: "aspect-[3/4]",
          width: 900,
          featured: true,
          caption: activeSlide.title
        },
        {
          src: GALLERIES.renovering.images[1],
          alt: "Renovering i et moderne dansk hjem",
          ratio: "aspect-[4/3]",
          width: 700
        },
        {
          src: GALLERIES.traeterrasse.images[0],
          alt: "Træterrasse i naturlige materialer",
          ratio: "aspect-[4/5]",
          width: 700
        }
      ]
    },
    {
      animationClass: "lg:motion-safe:animate-[hero-mosaic-down_32s_linear_infinite]",
      tiles: [
        {
          src: GALLERIES["doere-og-vinduer"].images[0],
          alt: "Døre og vinduer monteret i moderne bolig",
          ratio: "aspect-[4/3]",
          width: 700
        },
        {
          src: GALLERIES["til-og-ombygning"].images[0],
          alt: "Ombygning med fokus på lys og plads",
          ratio: "aspect-[3/4]",
          width: 700
        },
        {
          src: GALLERIES.tagarbejde.images[0],
          alt: "Tagarbejde udført med præcision",
          ratio: "aspect-[4/5]",
          width: 700
        }
      ]
    },
    {
      animationClass: "lg:motion-safe:animate-[hero-mosaic-up_40s_linear_infinite]",
      tiles: [
        {
          src: GALLERIES.tagarbejde.images[1],
          alt: "Tagarbejde og renovering af tag",
          ratio: "aspect-[4/5]",
          width: 700
        },
        {
          src: GALLERIES.renovering.images[2],
          alt: "Renoveringsprojekt i dansk bolig",
          ratio: "aspect-[4/3]",
          width: 700
        },
        {
          src: GALLERIES["til-og-ombygning"].images[3],
          alt: "Tilbygning og ombygning i Hasselager",
          ratio: "aspect-[3/4]",
          width: 700
        }
      ]
    }
  ];

  const metaTitle = pageData?.meta_title?.da || "Sehested Tømrerfirma ApS";
  const metaDesc = pageData?.meta_description?.da || "Tømrerfirma i Hasselager – Vi dækker hele østjylland. Kontakt Sehested Tømrerfirma ApS i dag!";

  return (
    <div className="relative">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
      </Helmet>

      {/* Dynamic Hero Banner */}
      <section className="relative overflow-hidden bg-primary-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(105,190,40,0.16),transparent_42%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(12,10,9,0.12)_0%,rgba(12,10,9,0.3)_100%)]" />

        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            {/* Left content */}
            <div className="lg:col-span-5 space-y-7">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-400/25 bg-brand-400/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-brand-300">
                <Star className="h-3.5 w-3.5" />
                {companyInfo.experience}
              </span>

              <h1 className="max-w-2xl font-display text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl leading-[1.02]">
                Tømrer i Hasselager og Aarhus
              </h1>


              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-primary-200">
                <span className="h-2 w-2 rounded-full bg-brand-400" />
                {activeSlide.title}
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Link
                  to="/kontakt"
                  className="rounded-full bg-brand-400 px-6 py-3.5 text-sm font-bold text-primary-950 shadow-lg shadow-brand-400/25 transition-all duration-200 hover:bg-brand-300 hover:scale-[1.02]"
                >
                  Få et uforpligtende tilbud
                </Link>
                <a
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:border-brand-400/60 hover:bg-white/15"
                >
                  <Phone className="h-4 w-4 text-brand-400" />
                  Ring {companyInfo.phone}
                </a>
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1 text-xs text-primary-300">
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand-400" />
                  Gratis & uforpligtende tilbud
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand-400" />
                  Dækket af Byg Garanti
                </span>
                <a
                  href="https://g.page/r/CS5RL2i4p0k0EBM/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors group"
                >
                  <span className="font-medium text-white group-hover:underline">Anmeld på Google</span>
                  <ExternalLink className="h-2.5 w-2.5 opacity-70 group-hover:opacity-100" />
                </a>
              </div>
            </div>

            {/* Right image reel */}
            <div className="lg:col-span-7">
              <div className="relative">
                <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-16 bg-gradient-to-b from-primary-950 to-transparent" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-28 bg-gradient-to-t from-primary-950 to-transparent" />
                <div className="grid h-[540px] gap-4 sm:h-[620px] lg:h-[720px] lg:grid-cols-3">
                  {heroMosaicColumns.map((column, columnIndex) => (
                    <div key={columnIndex} className="relative h-full overflow-hidden">
                      <div className={`flex flex-col gap-4 will-change-transform ${column.animationClass}`}>
                        {[...column.tiles, ...column.tiles].map((tile, tileIndex) => (
                          <div
                            key={`${columnIndex}-${tileIndex}`}
                            className={`relative overflow-hidden border border-white/10 bg-white/5 shadow-[0_20px_50px_rgba(0,0,0,0.24)] ${tile.ratio}`}
                          >
                            <img
                              src={getOptimizedImageUrl(tile.src, { width: tile.width, quality: 82 })}
                              alt={tile.alt}
                              className="h-full w-full object-cover"
                            />
                            {tile.featured ? (
                              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary-950/85 via-primary-950/40 to-transparent p-4">
                                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-brand-300">
                                  Aktuelt fokus
                                </p>
                                <p className="mt-1 text-sm font-semibold text-white">
                                  {tile.caption}
                                </p>
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Presentation Section */}
      <section className="relative overflow-hidden bg-primary-50 py-16 md:py-24">
        <div className="absolute inset-y-0 left-0 hidden w-full lg:block lg:w-1/2 bg-[linear-gradient(to_right,rgba(28,25,23,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(28,25,23,0.04)_1px,transparent_1px)] [background-size:26px_26px]" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2 lg:items-stretch">
          {/* Text Content */}
          <div className="relative border-b border-primary-200 bg-primary-50 px-4 py-12 sm:px-6 md:px-8 lg:border-b-0 lg:border-r lg:px-10 lg:py-16 xl:px-12">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-brand-700">
                <span className="h-px w-8 bg-brand-500" />
                Velkommen til Sehested Tømrerfirma
              </div>
              <h2 className="mt-5 max-w-lg font-display text-3xl font-bold tracking-tight text-primary-950 sm:text-4xl lg:text-[3.25rem] lg:leading-[1.04]">
                Din lokale <span className="text-brand-700">tømrer</span> til små og store opgaver
              </h2>
              <div className="mt-6 max-w-xl space-y-4 text-base leading-relaxed text-primary-700">
                <p>
                  Hos Sehested Tømrerfirma får du en erfaren og engageret tømrer med base i Hasselager – og kunder i hele Århus og omegn, herunder{" "}
                  <Link to="/toemrer-hoejbjerg" className="underline decoration-brand-400 underline-offset-2 hover:text-brand-700 transition-colors">Højbjerg</Link>,{" "}
                  <Link to="/toemrer-viby" className="underline decoration-brand-400 underline-offset-2 hover:text-brand-700 transition-colors">Viby</Link> og{" "}
                  <Link to="/toemrer-maarslet" className="underline decoration-brand-400 underline-offset-2 hover:text-brand-700 transition-colors">Mårslet</Link>. Vi leverer solidt håndværk og skræddersyede løsninger til både små og store opgaver – altid med personlig kontakt og høj kvalitet i fokus.
                </p>
                <p>
                  Jeg hedder Søren Sehested, og jeg står selv for alt arbejdet fra start til slut. Jeg har over 20 års erfaring med tømrerarbejde, og når du vælger mig til din renovering, er det mig, du snakker med – og mig, der står med værktøjet i hånden. Jeg har fingeren på det hele, og du slipper for mellemled eller misforståelser.
                </p>
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-none bg-brand-500 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.22em] text-primary-950 shadow-sm transition-all duration-200 hover:bg-brand-400"
                >
                  Få et uforpligtende tilbud
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <a
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="inline-flex items-center justify-center gap-2 rounded-none border border-primary-300 bg-white px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.22em] text-primary-900 transition-colors duration-200 hover:border-primary-400 hover:bg-primary-100"
                >
                  <Phone className="h-3.5 w-3.5 text-brand-600" />
                  Ring {companyInfo.phone}
                </a>
              </div>
              <div className="mt-4">
                <Link
                  to="/ydelser"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-brand-700 transition-colors"
                >
                  Se alle vores tømrerydelser & specialer
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>

          {/* Image Column */}
          <div className="relative min-h-[420px] lg:min-h-[760px]">
            <img
              src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00006-2-scaled.jpeg", { width: 900, height: 760 })}
              alt="Sehested Tømrerarbejde"
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(12,10,9,0.02)_0%,rgba(12,10,9,0.12)_100%)]" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="relative left-1/2 right-1/2 w-[100vw] -translate-x-1/2 overflow-hidden py-16 md:py-24 bg-white">
        <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-sm font-bold uppercase tracking-widest text-brand-600">Vores Ydelser</span>
            <h2 className="font-display text-3xl font-bold tracking-tight text-primary-950 sm:text-4xl">
              Professionelt tømrerarbejde til ethvert behov
            </h2>
            <p className="text-primary-600 text-base">
              Vi udfører en bred vifte af tømreropgaver med fokus på kvalitet, holdbarhed og personlig rådgivning.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-2">
            {servicesOverview.map((service, index) => (
              <article
                key={index}
                className="group flex h-full flex-col overflow-hidden border border-primary-200 bg-white shadow-sm lg:flex-row"
              >
                <div className="relative aspect-[4/3] overflow-hidden border-b border-primary-100 lg:w-[44%] lg:shrink-0 lg:border-b-0 lg:border-r lg:aspect-auto">
                  <img
                    src={getOptimizedImageUrl(service.image, { width: 800, height: 600 })}
                    alt={service.title}
                    className="h-full w-full object-cover"
                  />

                </div>
                <div className="flex flex-1 flex-col justify-between p-6 lg:w-[56%]">
                  <div className="space-y-3">
                    <h3 className="font-display text-xl font-bold text-primary-950">
                      {service.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-primary-600">
                      {service.desc}
                    </p>
                  </div>
                  <div className="pt-6 border-t border-primary-100">
                    <Link
                      to={service.path}
                      className="inline-flex items-center gap-2 rounded-full border border-primary-200 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-primary-950 transition-colors duration-200 hover:border-brand-400 hover:text-brand-700"
                    >
                      Læs mere
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-16 border border-primary-200/70 bg-primary-900 p-8 shadow-[0_20px_60px_rgba(16,24,40,0.08)] md:p-10">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-6 space-y-3">
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand-400">Ekstra ydelser</span>
                <h3 className="font-display text-xl font-bold text-white">Vi tilbyder også:</h3>
                <p className="text-sm leading-relaxed text-primary-200">
                  Ingen opgave er for lille. Vi hjælper dig også med mindre reparationer og specialopgaver.
                </p>
              </div>
              <div className="lg:col-span-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {secondaryServices.map((service, index) => (
                  <Link
                    key={index}
                    to={service.path}
                    className={service.highlight
                      ? 'group flex min-h-20 items-center justify-center border border-brand-300/70 bg-brand-400 px-5 py-5 text-center text-primary-950 shadow-lg shadow-brand-400/20 transition-all duration-200 hover:bg-brand-400 hover:shadow-lg hover:shadow-brand-400/20 animate-[subtle-shake_3s_ease-in-out_infinite]'
                      : 'flex min-h-20 items-center justify-center border border-white/10 bg-white/5 px-5 py-5 text-center text-white shadow-sm transition-all duration-200 hover:border-brand-400/40 hover:bg-white/10'}
                  >
                    <span className={service.highlight ? 'text-sm font-bold tracking-wide' : 'text-sm font-bold'}>{service.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mid-page Conversion Banner */}
      <section className="relative overflow-hidden bg-primary-950 px-4 py-12 text-white sm:px-6 sm:py-14 lg:px-8 border-y border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(105,190,40,0.12),transparent_70%)] pointer-events-none" />
        <div className="relative mx-auto flex max-w-7xl flex-col items-center justify-between gap-8 lg:flex-row text-center lg:text-left">
          <div className="max-w-2xl space-y-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.24em] text-brand-400">
              <Star className="h-3.5 w-3.5 fill-brand-400" />
              Klar til at starte dit projekt?
            </span>
            <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Få en professionel vurdering og et uforpligtende tilbud
            </h3>
            <p className="text-sm sm:text-base leading-relaxed text-primary-300">
              Vi gennemgår dine ønsker, rådgiver om de rigtige materialer og giver en fair og gennemskuelig pris på arbejdet.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
            <Link
              to="/kontakt"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand-400 px-7 py-3.5 text-sm font-bold text-primary-950 shadow-lg shadow-brand-400/20 transition-all duration-200 hover:bg-brand-300 hover:scale-[1.02]"
            >
              Få et uforpligtende tilbud
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`tel:${companyInfo.phoneRaw}`}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10 hover:border-white/40"
            >
              <Phone className="h-4 w-4 text-brand-400" />
              Ring {companyInfo.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Byg Garanti Sektion */}
      <section className="py-16 bg-primary-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-400">Din Tryghed</span>
              <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Medlem af Byg Garanti & DI Byggeri
              </h2>
              <p className="text-primary-200 text-base leading-relaxed">
                Når du vælger Sehested Tømrerfirma ApS, vælger du et tømrerfirma, der er medlem af DI Byggeri og dækket af Byg Garanti. Det er din personlige og økonomiske sikkerhed for, at det udførte arbejde lever op til alle gældende standarder og krav.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-primary-200">
                <li className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-5 w-5 text-brand-400 shrink-0" />
                  <span>Dækker mod fejl og mangler</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-5 w-5 text-brand-400 shrink-0" />
                  <span>Sikkerhed op til 150.000 kr.</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-5 w-5 text-brand-400 shrink-0" />
                  <span>Professionelt kvalitetssikret</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-5 w-5 text-brand-400 shrink-0" />
                  <span>Gælder i op til 10 år efter</span>
                </li>
              </ul>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-400 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-primary-950 shadow-md transition-colors hover:bg-brand-300"
                >
                  Byg med garanti – Få et tilbud
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white hover:bg-white/10 transition-colors"
                >
                  <Phone className="h-3.5 w-3.5 text-brand-400" />
                  Ring {companyInfo.phone}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5 flex flex-col items-center justify-center gap-6 py-2">
              <img
                src={companyInfo.bygGarantiLogo}
                alt="Byg Garanti"
                className="h-24 w-auto object-contain filter brightness-100"
              />
              <img
                src={companyInfo.diByggeriLogo}
                alt="DI Byggeri"
                className="h-[65px] w-auto object-contain filter brightness-100"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="max-w-4xl space-y-5">
              <span className="inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-4 py-1 text-xs font-bold uppercase tracking-[0.24em] text-primary-700">
                Tidligere projekter
              </span>
              <h2 className="max-w-3xl font-display text-4xl font-bold tracking-tight text-primary-950 sm:text-5xl lg:text-6xl">
                Se vores seneste <span className="text-brand-600">arbejde</span>
              </h2>
            </div>
            <Link
              to="/tidligere-projekter"
              className="inline-flex items-center gap-2 self-start rounded-full border border-primary-200 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.24em] text-primary-950 transition-colors duration-200 hover:border-brand-400 hover:text-brand-700 md:self-auto"
            >
              Se alle projekter
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {galleryData && galleryData.images ? (() => {
            const projectImages = galleryData.images.filter(Boolean);
            if (!projectImages.length) {
              return (
                <div className="flex h-48 items-center justify-center text-primary-400">
                  Indlæser projekter...
                </div>
              );
            }

            const featuredImage = projectImages[projectSlide % projectImages.length];
            const supportingImages = Array.from({ length: 4 }, (_, index) => projectImages[(projectSlide + index + 1) % projectImages.length]);

            return (
              <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr] xl:items-stretch">
                <div className="relative min-h-[420px] overflow-hidden border border-primary-100 bg-primary-100 md:min-h-[560px]">
                  <img
                    key={featuredImage}
                    src={getOptimizedImageUrl(featuredImage, { width: 1100, height: 1200, fit: "cover" })}
                    alt="Udvalgt projekt"
                    className="project-gallery-slide h-full w-full object-cover"
                  />
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  {supportingImages.map((image, index) => (
                    <div
                      key={`${image}-${index}`}
                      className="relative min-h-[180px] overflow-hidden border border-primary-100 bg-primary-100 md:min-h-[240px]"
                    >
                      <img
                        key={image}
                        src={getOptimizedImageUrl(image, { width: 700, height: 520, fit: "cover" })}
                        alt={`Projekt ${index + 2}`}
                        className={`project-gallery-slide ${index % 2 === 0 ? "project-gallery-slide-delay" : "project-gallery-slide-slow"} h-full w-full object-cover`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })() : (
            <div className="flex h-48 items-center justify-center text-primary-400">
              Indlæser projekter...
            </div>
          )}

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/tidligere-projekter"
              className="inline-flex items-center gap-2 rounded-full border border-primary-300 bg-white px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] text-primary-950 transition-colors duration-200 hover:bg-primary-50"
            >
              Se alle projekter
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full bg-brand-400 px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] text-primary-950 shadow-md transition-colors duration-200 hover:bg-brand-300"
            >
              Få tilbud på dit projekt
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Google Reviews & Kundetilfredshed Section */}
      <section className="border-t border-primary-200/80 bg-gradient-to-b from-white via-primary-50/40 to-amber-50/20 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-2xl border border-primary-200/90 bg-white p-7 sm:p-10 lg:p-12 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
              <div className="space-y-4 max-w-2xl">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-xs border border-primary-100 p-2.5 shrink-0">
                    <svg className="h-full w-full" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                      <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-bold text-primary-950">Google Anmeldelser</span>
                    </div>
                    <p className="text-xs text-primary-500 font-medium">Sehested Tømrerfirma • Hasselager & Aarhus</p>
                  </div>
                </div>

                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary-950">
                    Har vi hjulpet dig med et bygge- eller renoveringsprojekt?
                  </h2>
                  <p className="mt-3 text-sm sm:text-base text-primary-700 leading-relaxed">
                    Vi sætter en stor ære i personlig kontakt, solidt håndværk og glade kunder. Har du samarbejdet med Søren Sehested, sætter vi stor pris på, hvis du vil bruge et øjeblik på at dele din oplevelse med en anmeldelse på Google. Det hjælper andre boligejere i Hasselager, Aarhus og omegn med at vælge den rette tømrer.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-y-2 gap-x-5 text-xs text-primary-600 pt-1">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-brand-600" /> Tager under 1 minut
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-brand-600" /> Direkte på Google
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-brand-600" /> Hjælper lokale boligejere
                  </span>
                </div>
              </div>

              <div className="shrink-0 flex flex-col sm:flex-row lg:flex-col items-stretch sm:items-center lg:items-stretch gap-3">
                <a
                  href="https://g.page/r/CS5RL2i4p0k0EBM/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2.5 rounded-full bg-primary-950 hover:bg-brand-600 px-7 py-4 text-sm font-bold text-white transition-all duration-200 shadow-md group cursor-pointer"
                >
                  <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                    <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                  </svg>
                  <span>Giv en Google anmeldelse</span>
                  <ExternalLink className="h-4 w-4 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </a>
                <p className="text-center text-xs text-primary-500">
                  Åbner direkte i Google Reviews
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO & Faglig rådgivning Section */}
      <section className="border-t border-primary-200/80 bg-primary-50/50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Main Headline & Ingress */}
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.22em] text-brand-700 shadow-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
              Kvalitet & godt håndværk
            </span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-primary-950 sm:text-4xl lg:text-[2.65rem] lg:leading-[1.12]">
              Tømrerarbejde der gør boligen bedre i hverdagen
            </h2>
            <p className="mt-5 text-base leading-relaxed text-primary-700 sm:text-lg">
              Som tømrer løser jeg opgaver, der kan forbedre boligens udtryk, funktion og brug af plads. Sehested Tømrerfirma udfører blandt andet tilbygning, træterrasse, døre og vinduer samt tagarbejde. Opgaven bliver vurderet ud fra huset, dine ønsker og det arbejde, der konkret skal udføres.
            </p>
          </div>

          {/* 3 Content Cards */}
          <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Card 1: Døre, vinduer, tag og træterrasse */}
            <div className="flex flex-col justify-between border border-primary-200 bg-white p-7 shadow-xs sm:p-8">
              <div className="space-y-4">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-none bg-brand-50 text-brand-700 border border-brand-200/60">
                  <CheckCircle2 className="h-5 w-5 text-brand-600" />
                </div>
                <h3 className="font-display text-xl font-bold leading-snug text-primary-950">
                  Døre, vinduer, tag og træterrasse kræver blik for detaljen
                </h3>
                <div className="space-y-3 text-sm leading-relaxed text-primary-700">
                  <p>
                    Skal vinduer og døre udskiftes, taget have udført arbejde eller haven have en træterrasse, er præcis udførelse vigtig for et holdbart og pænt resultat. Jeg tager udgangspunkt i boligens konstruktion og den ønskede løsning, så arbejdet passer naturligt til de eksisterende rammer.
                  </p>
                  <p>
                    Et tidligere projekt omfattede levering og montering af specialfremstillede vinduer fra Horn Vinduesfabrik. Her var korrekt tilpasning og omhyggelig montering centrale dele af opgaven. Det er et godt eksempel på det fokus på præcision, som også følger mine øvrige tømreropgaver.
                  </p>
                </div>
              </div>
              <div className="mt-6 border-t border-primary-100 pt-5 flex items-center justify-between gap-3">
                <Link
                  to="/tidligere-projekter/horn-vinduesfabrik"
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-700 transition-colors duration-200 hover:text-brand-900"
                >
                  Se projektet
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-1 rounded-full bg-primary-950 px-3.5 py-1.5 text-[0.7rem] font-bold uppercase tracking-wider text-white hover:bg-primary-800 transition-colors"
                >
                  Få tilbud
                </Link>
              </div>
            </div>

            {/* Card 2: Ombygning, totalrenovering og totalentreprise */}
            <div className="flex flex-col justify-between border border-primary-200 bg-white p-7 shadow-xs sm:p-8">
              <div className="space-y-4">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-none bg-brand-50 text-brand-700 border border-brand-200/60">
                  <CheckCircle2 className="h-5 w-5 text-brand-600" />
                </div>
                <h3 className="font-display text-xl font-bold leading-snug text-primary-950">
                  Ombygning, totalrenovering og totalentreprise til større planer
                </h3>
                <div className="space-y-3 text-sm leading-relaxed text-primary-700">
                  <p>
                    Ved en ombygning kan du ændre rumfordelingen eller skabe en bolig, der passer bedre til hverdagen. En{" "}
                    <Link to="/totalrenovering" className="font-medium text-brand-700 hover:text-brand-900 underline underline-offset-2 transition-colors">
                      totalrenovering
                    </Link>{" "}
                    er relevant, når flere dele af huset skal fornyes i samme projekt. Jeg hjælper med begge typer opgaver og tager udgangspunkt i boligens stand og omfanget af dine planer.
                  </p>
                  <p>
                    Jeg tilbyder også{" "}
                    <Link to="/totalentreprise" className="font-medium text-brand-700 hover:text-brand-900 underline underline-offset-2 transition-colors">
                      totalentreprise
                    </Link>{" "}
                    til relevante byggeprojekter samt hjælp til tilbygning, når boligen skal have flere kvadratmeter. Rammerne for opgaven aftales konkret, så du ved, hvad mit arbejde omfatter. På den måde tager planlægningen afsæt i det projekt, du faktisk står med.
                  </p>
                </div>
              </div>
              <div className="mt-6 border-t border-primary-100 pt-5 flex items-center justify-between gap-3">
                <Link
                  to="/ydelser"
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-700 transition-colors duration-200 hover:text-brand-900"
                >
                  Udforsk alle ydelser
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-1 rounded-full bg-primary-950 px-3.5 py-1.5 text-[0.7rem] font-bold uppercase tracking-wider text-white hover:bg-primary-800 transition-colors"
                >
                  Få tilbud
                </Link>
              </div>
            </div>

            {/* Card 3: Lokal tømrer i Hasselager og Aarhus samt byerne omkring */}
            <div className="flex flex-col justify-between border border-primary-200 bg-white p-7 shadow-xs sm:p-8">
              <div className="space-y-4">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-none bg-brand-50 text-brand-700 border border-brand-200/60">
                  <CheckCircle2 className="h-5 w-5 text-brand-600" />
                </div>
                <h3 className="font-display text-xl font-bold leading-snug text-primary-950">
                  Lokal tømrer i Hasselager og Aarhus samt byerne omkring
                </h3>
                <div className="space-y-3 text-sm leading-relaxed text-primary-700">
                  <p>
                    Leder du efter en tømrer i Hasselager eller en tømrer i Aarhus, kan jeg hjælpe med dit projekt. Jeg arbejder især i Aarhus Syd og kører også til{" "}
                    <Link to="/toemrer-hoejbjerg" className="font-medium text-primary-900 underline decoration-brand-400 underline-offset-2 hover:text-brand-700 transition-colors">Højbjerg</Link>,{" "}
                    <Link to="/toemrer-viby" className="font-medium text-primary-900 underline decoration-brand-400 underline-offset-2 hover:text-brand-700 transition-colors">Viby</Link>,{" "}
                    <Link to="/toemrer-maarslet" className="font-medium text-primary-900 underline decoration-brand-400 underline-offset-2 hover:text-brand-700 transition-colors">Mårslet</Link>,{" "}
                    <Link to="/toemrer-skanderborg" className="font-medium text-primary-900 underline decoration-brand-400 underline-offset-2 hover:text-brand-700 transition-colors">Skanderborg</Link> og{" "}
                    <Link to="/toemrer-odder" className="font-medium text-primary-900 underline decoration-brand-400 underline-offset-2 hover:text-brand-700 transition-colors">Odder</Link>. Virksomheden har base i Hasselager, så du får en lokal tømrer.
                  </p>
                  <p>
                    Behovet kan være en mindre tømreropgave, en ombygning af huset eller en gennemgribende totalrenovering. Uanset størrelsen begynder et godt forløb med en konkret samtale om ønsker og muligheder. Jeg lægger vægt på, at løsningen giver mening for både boligen og din hverdag.
                  </p>
                </div>
              </div>
              <div className="mt-6 border-t border-primary-100 pt-4">
                <p className="text-[0.7rem] font-bold uppercase tracking-[0.2em] text-primary-500 mb-2">
                  Dækningsområde:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { name: "Hasselager", path: "/" },
                    { name: "Aarhus", path: "/" },
                    { name: "Aarhus Syd", path: "/" },
                    { name: "Højbjerg", path: "/toemrer-hoejbjerg" },
                    { name: "Viby", path: "/toemrer-viby" },
                    { name: "Mårslet", path: "/toemrer-maarslet" },
                    { name: "Skanderborg", path: "/toemrer-skanderborg" },
                    { name: "Odder", path: "/toemrer-odder" }
                  ].map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      className="inline-block rounded-none border border-primary-200/80 bg-primary-50 px-2 py-0.5 text-xs text-primary-800 transition-colors hover:border-brand-500 hover:bg-brand-50 hover:text-brand-900"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-primary-100">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center justify-center gap-2 w-full rounded-full bg-brand-400 hover:bg-brand-300 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-primary-950 transition-colors shadow-xs"
                  >
                    Kontakt din lokale tømrer
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Quick conversion hook */}
          <div className="mt-12 rounded-2xl bg-white border border-primary-200 p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
            <div className="space-y-1 text-center md:text-left">
              <h3 className="font-display text-lg sm:text-xl font-bold text-primary-950">
                Klar til at forbedre din bolig?
              </h3>
              <p className="text-sm text-primary-600">
                Uanset om du planlægger en større ombygning eller udskiftning af vinduer, er første skridt en uforpligtende samtale.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full border border-primary-300 bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-primary-900 hover:bg-primary-50 transition-colors"
              >
                <Phone className="h-3.5 w-3.5 text-brand-600" />
                Ring {COMPANY_INFO.phone}
              </a>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full bg-brand-400 hover:bg-brand-300 px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-primary-950 transition-colors shadow-sm"
              >
                Skriv til mig
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section with Form */}
      <section id="kontakt" className="py-16 md:py-24 bg-white border-t border-primary-200/70 scroll-mt-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            {/* Contact Info / Fortæl mig hvad du gerne vil ændre */}
            <div className="lg:col-span-12 space-y-8">
              <div className="space-y-4 max-w-3xl">
                <span className="text-sm font-bold uppercase tracking-widest text-brand-600">Kontakt & Samtale</span>
                <h2 className="font-display text-3xl font-bold tracking-tight text-primary-950 sm:text-4xl">
                  Fortæl mig hvad du gerne vil ændre i din bolig
                </h2>
                <p className="text-primary-700 text-base sm:text-lg leading-relaxed">
                  Har du brug for en tømrer, er du velkommen til at kontakte Sehested Tømrerfirma og fortælle om opgaven. Beskriv gerne boligen, dine ønsker og hvor omfattende projektet er. Så kan vi tage en konkret snak om det videre forløb og finde ud af, hvordan jeg bedst kan hjælpe dig.
                </p>
              </div>

              {/* Quick Contact Options */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="flex items-center gap-3 p-4 rounded-xl border border-primary-200 bg-primary-50/60 hover:bg-primary-100 hover:border-brand-400 transition-all group"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-400 text-primary-950 shrink-0">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-primary-500 uppercase tracking-wider">Ring direkte</p>
                    <p className="text-sm font-bold text-primary-950 group-hover:text-brand-700">{COMPANY_INFO.phone}</p>
                  </div>
                </a>

                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="flex items-center gap-3 p-4 rounded-xl border border-primary-200 bg-primary-50/60 hover:bg-primary-100 hover:border-brand-400 transition-all group"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-400 text-primary-950 shrink-0">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-primary-500 uppercase tracking-wider">Send en e-mail</p>
                    <p className="text-sm font-bold text-primary-950 group-hover:text-brand-700">{COMPANY_INFO.email}</p>
                  </div>
                </a>

                <div className="flex items-center gap-3 p-4 rounded-xl border border-primary-200 bg-primary-50/60">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-200 text-primary-800 shrink-0">
                    <CheckCircle2 className="h-4 w-4 text-brand-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-primary-500 uppercase tracking-wider">Svartid</p>
                    <p className="text-sm font-bold text-primary-950">Hurtig tilbagemelding</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-12">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
